// src/lib/analytics.js
//
// Unified GA4 + Meta Pixel client. The frontend calls semantic helpers
// (pageView, trackCTA, trackSignup, …) and this module dispatches the right
// event names to each platform so we follow each vendor's best practices:
//
// - GA4: standard event names (page_view, sign_up, login, select_content,
//   scroll) so the events show up in built-in reports and explorer.
// - Meta Pixel: standard events (PageView, ViewContent, Lead,
//   CompleteRegistration) so Ads Manager can optimize "Leads" / "Signups"
//   objectives against high-intent actions instead of generic custom events.
//
// UTMs are captured once on first landing-page hit and persisted in
// localStorage for 30 days so multi-session attribution survives the Auth0
// redirect round-trip and revisits within a typical campaign window. Every
// Meta event carries an `event_id` (UUID) so server-side Conversions API
// events can be de-duplicated later without changing this code.

const GA4_ID = 'G-T5ZSQR9LVN';
const META_PIXEL_ID = '1703688980807797';

const UTM_STORAGE_KEY = 'mom3ntum_utm_v2';
const UTM_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

let initialized = false;
const scrollDepthFired = new Set();

function ga(...args) {
  if (typeof window.gtag === 'function') {
    window.gtag(...args);
  }
}

function fb(...args) {
  if (typeof window.fbq === 'function') {
    window.fbq(...args);
  }
}

function uuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for very old browsers — collision-safe enough for analytics dedup.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// SHA-256 hash for Meta advanced matching / GA4 user_id without leaking PII.
// Meta requires hashed PII (lowercase, trimmed) for advanced matching.
export async function sha256Hex(value) {
  if (!value || typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  const buf = new TextEncoder().encode(normalized);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function getStoredUTM() {
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    if (parsed.ts && Date.now() - parsed.ts > UTM_TTL_MS) {
      localStorage.removeItem(UTM_STORAGE_KEY);
      return {};
    }
    return parsed.utm || {};
  } catch {
    return {};
  }
}

export function init() {
  if (initialized) return;
  initialized = true;

  // GA4
  if (GA4_ID && GA4_ID !== 'G-XXXXXXXXXX') {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    ga('js', new Date());
    // send_page_view=false because we call pageView() ourselves with the
    // logical page name; otherwise GA4 fires an additional automatic event.
    ga('config', GA4_ID, { send_page_view: false });
  }

  // Meta Pixel
  if (META_PIXEL_ID) {
    !function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v;
      s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    fb('init', META_PIXEL_ID);
    // Initial PageView with a dedicated event_id so the future CAPI server
    // event can dedup against this one.
    fb('track', 'PageView', {}, { eventID: uuid() });
  }
}

// Reads UTM params from the URL on landing and stores them with a 30-day TTL
// so subsequent events (CTA clicks, signups) carry attribution even after the
// Auth0 redirect round-trip wipes the URL.
export function captureUTM() {
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  let hasAny = false;
  for (const key of UTM_PARAMS) {
    const val = params.get(key);
    if (val) {
      utm[key] = val;
      hasAny = true;
    }
  }
  if (hasAny) {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify({ utm, ts: Date.now() }));
  }
  return utm;
}

export function getUTM() {
  return getStoredUTM();
}

export function pageView(pageName) {
  const utm = getStoredUTM();
  // GA4: configure-time send_page_view=false means we own this event.
  ga('event', 'page_view', { page_title: pageName, page_location: window.location.href, ...utm });
  // Meta: initial PageView already fired in init(); only re-fire for SPA route
  // changes the user is logically landing on for the first time. We rely on
  // SPA route guards to call this when appropriate.
  fb('track', 'PageView', { page: pageName }, { eventID: uuid() });
}

/**
 * Track a primary call-to-action click.
 *
 * @param {string} ctaName  Stable name (e.g., "hero", "footer_cta").
 * @param {{position?: 'header'|'hero'|'mid'|'footer'}} [meta]  Optional context.
 *
 * GA4: select_content (standard event, ideal for "user chose to engage").
 * Meta: Lead (high-intent — Meta's "Leads" campaign objective optimizes
 *       toward this event, not toward generic ViewContent).
 */
export function trackCTA(ctaName, meta = {}) {
  const utm = getStoredUTM();
  const position = meta.position || ctaName; // sensible default
  ga('event', 'select_content', {
    content_type: 'cta',
    content_id: ctaName,
    cta_position: position,
    ...utm,
  });
  fb('track', 'Lead', {
    content_name: ctaName,
    content_category: position,
    ...utm,
  }, { eventID: uuid() });
}

export function trackSectionView(sectionName) {
  ga('event', 'section_view', { section_name: sectionName });
}

// GA4 standard `scroll` event at 25/50/75/100% milestones — fires at most once
// per milestone per page load. Called by attachScrollDepth() below.
function fireScrollDepth(percent) {
  if (scrollDepthFired.has(percent)) return;
  scrollDepthFired.add(percent);
  ga('event', 'scroll', { percent_scrolled: percent });
}

/**
 * Attach a scroll-depth listener. Returns a cleanup function. Call once per
 * page (LandingV3 mounts it in useEffect). Tracks 25/50/75/100% depth.
 */
export function attachScrollDepth() {
  scrollDepthFired.clear();
  const onScroll = () => {
    const h = document.documentElement;
    const scrolled = (h.scrollTop + window.innerHeight) / h.scrollHeight;
    const pct = Math.floor(scrolled * 100);
    if (pct >= 25) fireScrollDepth(25);
    if (pct >= 50) fireScrollDepth(50);
    if (pct >= 75) fireScrollDepth(75);
    if (pct >= 95) fireScrollDepth(100);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  return () => window.removeEventListener('scroll', onScroll);
}

/**
 * Fire conversion events after a successful Auth0 signup. Accepts the new
 * user's email so we can attach hashed advanced-matching data to Meta —
 * dramatically improves attribution under iOS/ITP cookie restrictions.
 */
export async function trackSignup({ email } = {}) {
  const utm = getStoredUTM();
  const hashedEmail = email ? await sha256Hex(email) : null;

  ga('event', 'sign_up', { method: 'auth0', ...utm });
  if (hashedEmail) {
    // Enhanced conversions / cross-session user identification.
    ga('set', 'user_properties', { hashed_email: hashedEmail });
  }

  if (hashedEmail) {
    fb('init', META_PIXEL_ID, { em: hashedEmail }); // advanced matching
  }
  fb('track', 'CompleteRegistration', {
    method: 'auth0',
    ...utm,
  }, { eventID: uuid() });
}

export async function trackLogin({ email } = {}) {
  const hashedEmail = email ? await sha256Hex(email) : null;
  ga('event', 'login', { method: 'auth0' });
  if (hashedEmail) {
    ga('set', 'user_properties', { hashed_email: hashedEmail });
    fb('init', META_PIXEL_ID, { em: hashedEmail });
  }
}
